import React from 'react';
import {Box, Divider} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {Field} from './Field';
import {Section} from './Section';
import {TripPanel} from './TripPanel';
import moment from "moment";
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import {useTranslation} from "@/Utils/CustomHooks/useTranslation";


export interface BookingContentProps {
    booking: any,
    loading?: boolean;
}

/*
 * German postal format: "Street, 12345 City". Parts the booking does not carry
 * are dropped instead of being concatenated -- the delivery/pickup address
 * fields are not in the schema yet, so plain concatenation rendered
 * "undefined, undefined undefined". Returns undefined when nothing is left,
 * which makes TripPanel omit the address line entirely.
 */
function formatAddress(
    street?: string | null,
    zipcode?: string | null,
    city?: string | null,
): string | undefined {
    const place = [zipcode, city].filter(Boolean).join(' ');
    const address = [street, place].filter(Boolean).join(', ');

    return address || undefined;
}

export function BookingInformationContent({booking, loading}: BookingContentProps) {
    const {t} = useTranslation();

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2.25}}>
            {/* Vertrag */}
            <Section title={t("bookingLine.contract", "Vertrag")}>
                <Field label={t("bookingLine.bookingId", "Vertragsnummer")} loading={loading} skeletonWidth={110}
                       strong>
                    {booking?.bookingId}
                </Field>
                <Field label={t("bookingLine.reservationNumber", "Reservierungsnummer")} loading={loading}
                       skeletonWidth={140}
                       empty={!booking?.reservationNumber}>
                    {booking?.reservationNumber}
                </Field>
                <Field label={t("bookingLine.reservedGroup", "Reservierte Gruppe")} loading={loading} skeletonWidth={70}
                       empty={!booking?.plannedVehicleGroup}>
                    {booking?.plannedVehicleGroup?.vehicleGroupId}
                </Field>
            </Section>

            <Divider/>

            {/* Zeitraum */}
            <Section title={t("bookingLine.period", "Zeitraum")}>
                <Box sx={{display: 'flex', alignItems: 'stretch', gap: 1.5}}>
                    <TripPanel
                        kicker={t("bookingLine.departure", "Abfahrt")}
                        loading={loading}
                        station={booking?.startstation}
                        date={
                            moment(
                                booking?.startdate
                            ).format("DD.MM.YYYY")
                        }
                        time={
                            moment(
                                booking?.startdate
                            ).format("HH:mm")
                        }
                        extra={booking?.deliveryDate ? {
                            icon: <LocalShippingIcon/>, label: t("bookingLine.delivery", "Zustellung"),
                            address: formatAddress(
                                booking?.deliveryStreet,
                                booking?.deliveryZipcode,
                                booking?.deliveryCity,
                            ),
                            time: moment(booking?.deliveryDate).format("DD.MM.YYYY HH:mm"),
                        } : null}
                    />

                    <Box sx={{
                        flex: '0 0 auto', display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', color: 'rgba(0,0,0,.4)'
                    }}>
                        <ArrowForwardIcon sx={{fontSize: 20, color: 'rgba(0,0,0,.35)'}}/>
                    </Box>
                    {booking?.endstation !== null ? (
                        <TripPanel
                            kicker={t("bookingLine.arrival", "Ankunft")}
                            loading={loading}
                            station={booking?.endstation}
                            date={
                                moment(
                                    booking?.enddate
                                ).format("DD.MM.YYYY")
                            }
                            time={
                                moment(
                                    booking?.enddate
                                ).format("HH:mm")
                            }
                            extra={booking?.pickupDate ? {
                                icon: <LocalShippingIcon/>,
                                label: t("bookingLine.pickUp", "Abholung"),
                                address: formatAddress(
                                    booking?.pickupStreet,
                                    booking?.pickupZipcode,
                                    booking?.pickupCity,
                                ),
                                time: moment(booking?.pickupDate).format("DD.MM.YYYY HH:mm"),
                            } : null}
                        />
                    ) : (
                        <TripPanel
                            kicker={t("bookingLine.plannedArrival", "Geplante Ankunft")}
                            loading={loading}
                            station={booking?.plannedEndstation}
                            date={
                                moment(
                                    booking?.plannedEnddate
                                ).format("DD.MM.YYYY")
                            }
                            time={
                                moment(
                                    booking?.plannedEnddate
                                ).format("HH:mm")
                            }
                            extra={booking?.pickupDate ? {
                                icon: <LocalShippingIcon/>, label: t("bookingLine.pickUp", "Abholung"),
                                address: formatAddress(
                                    booking?.pickupStreet,
                                    booking?.pickupZipcode,
                                    booking?.pickupCity,
                                ),
                                time: moment(booking?.pickupDate).format("DD.MM.YYYY HH:mm"),
                            } : null}
                        />
                    )}
                </Box>
            </Section>

            <Divider/>

            {/* Konditionen */}
            <Section title={t("bookingLine.conditions", "Konditionen")}>
                <Field label={t("bookingLine.rate", "Tarif")} loading={loading} skeletonWidth={40}
                       strong>{booking?.rate}</Field>
                <Field label={t("bookingLine.miscellaneous", "Sonstiges")} loading={loading} skeletonWidth={150}
                       empty={!booking?.divdata}>
                    {booking?.divdata}
                </Field>

            </Section>
        </Box>
    );
}
